import { ActionItem, LeaseLifecycle, Process } from '@urbanmix-tech/shared-js';
import { ActionGroupList } from './TrackerActionGroupList';

import {
  setApartmentData,
  setHoveredActionItem,
  setVisualizer,
  setSelectedUnit,
  setHighlightedUnits,
} from '@/features/project/projectSlice';

import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useState, useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  Checkbox,
  FormControlLabel,
  Typography,
  Stack,
  InputLabel,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';

import { visualizeByLeaseRenewalState } from '@/processes/AnalyzeUnits';
import { useProcessSettings } from '@/shared/context/ProcessSettingsProvider';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { GenericDistinctVisualizer } from './BuildingVisualizers';
import { getDatePeriod, DATE_PERIOD_COLORS, DATE_PERIOD_CONFIGS, DatePeriod } from '@/constants/datePeriods';

type VisualizationMode = 'date' | 'process' | 'process_status' | 'user';

// Process status colors
const PROCESS_STATUS_COLORS: Record<string, string> = {
  'start': '#10B981',      // Green - Active/Running
  'open': '#3B82F6',       // Blue - Open/Ready to start
  'completed': '#8B5CF6',  // Purple - Completed
  'cancelled': '#EF4444',  // Red - Cancelled
  'paused': '#F59E0B',     // Amber - Paused
};

// Filter persistence
const STORAGE_KEY = 'allTasksFilters';

interface StoredFilters {
  visualizationMode: VisualizationMode;
  sortOrder: string;
  timeSpan: string;
  selectedStatus: string[];
  selectedProcessTypes: string[];
  selectedProcessStatus: string[];
  selectedOwners: string[];
  knownStatus: string[];
  knownProcessTypes: string[];
  knownProcessStatus: string[];
  knownOwners: string[];
}

const defaultFilters: StoredFilters = {
  visualizationMode: 'date',
  sortOrder: 'createdAt',
  timeSpan: 'all',
  selectedStatus: [],
  selectedProcessTypes: [],
  selectedProcessStatus: [],
  selectedOwners: [],
  knownStatus: [],
  knownProcessTypes: [],
  knownProcessStatus: [],
  knownOwners: [],
};

function loadFilters(): StoredFilters {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultFilters, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load all tasks filters:', e);
  }
  return defaultFilters;
}

function saveFilters(filters: StoredFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    console.warn('Failed to save all tasks filters:', e);
  }
}

type MultiSelectSelectedKey = 'selectedStatus' | 'selectedProcessTypes' | 'selectedProcessStatus' | 'selectedOwners';
type MultiSelectKnownKey = 'knownStatus' | 'knownProcessTypes' | 'knownProcessStatus' | 'knownOwners';

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter((v) => typeof v === 'string' && v.length > 0)));
}

function intersectPreserveOrder(values: string[], allowed: Set<string>): string[] {
  return values.filter((v) => allowed.has(v));
}

function useAutoSyncMultiSelect(params: {
  filters: StoredFilters | null;
  setFilters: Dispatch<SetStateAction<StoredFilters | null>>;
  available: string[];
  selectedKey: MultiSelectSelectedKey;
  knownKey: MultiSelectKnownKey;
}) {
  const { filters, setFilters, available, selectedKey, knownKey } = params;
  const prevAvailableRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!filters) return;

    // Ignore transient empty option lists (common during loading/re-fetch).
    // Otherwise we can accidentally clear selections and break auto-add.
    const availableUnique = uniqueStrings(available);
    if (availableUnique.length === 0) return;

    const currentSet = new Set(availableUnique);

    setFilters((prev) => {
      if (!prev) return prev;

      const prevKnownArr = uniqueStrings(prev[knownKey] ?? []);
      const baselineSet = prevKnownArr.length > 0
        ? new Set(prevKnownArr)
        : (prevAvailableRef.current ?? new Set<string>());

      const prevSelectedArr = uniqueStrings(prev[selectedKey] ?? []);
      const prevSelectedValid = intersectPreserveOrder(prevSelectedArr, currentSet);
      const hadSelection = prevSelectedValid.length > 0;

      const added = availableUnique.filter((v) => !baselineSet.has(v));
      const removed = Array.from(baselineSet).filter((v) => !currentSet.has(v));

      // Always prune disappeared values
      let nextSelected = prevSelectedValid;

      // Auto-add newly appeared values only if user already had a selection
      if (hadSelection && added.length > 0) {
        const existing = new Set(nextSelected);
        nextSelected = [...nextSelected, ...added.filter((v) => !existing.has(v))];
      }

      // If nothing changed and known is already up to date, bail.
      const selectedSame =
        nextSelected.length === (prev[selectedKey] ?? []).length &&
        nextSelected.every((v, i) => v === (prev[selectedKey] ?? [])[i]);
      const knownSame =
        prev[knownKey].length === availableUnique.length &&
        prev[knownKey].every((v, i) => v === availableUnique[i]);

      if (added.length === 0 && removed.length === 0 && selectedSame && knownSame) return prev;

      return {
        ...prev,
        [selectedKey]: nextSelected,
        [knownKey]: availableUnique,
      } as StoredFilters;
    });

    prevAvailableRef.current = currentSet;
  }, [filters, available, selectedKey, knownKey, setFilters]);
}

type TrackerTabContentProps = {
  actionItems: ActionItem[];
  processes: Process[];
  leaseStatusFilter: LeaseLifecycle[];
  onClickActionItem(actionItem: ActionItem): void;
  onBuildingDataChange?: (data: Record<string, any>) => void;
  isLoading?: boolean;
};

export function TrackerTabContent({
  actionItems,
  processes,
  onClickActionItem,
  onBuildingDataChange,
  isLoading = false,
}: TrackerTabContentProps) {
  const dispatch = useDispatch();
  const { getProcessTypeConfig } = useProcessSettings();

  // Filter state with persistence
  const filtersLoaded = useRef(false);
  const [filters, setFilters] = useState<StoredFilters | null>(null);

  // Load filters from localStorage on mount
  useEffect(() => {
    const stored = loadFilters();
    setFilters(stored);
    filtersLoaded.current = true;
  }, []);

  // Persist filters when they change (only after initial load)
  useEffect(() => {
    if (!filtersLoaded.current || !filters) return;
    saveFilters(filters);
  }, [filters]);

  // Destructure filters for easier access (with defaults for initial render)
  const visualizationMode = filters?.visualizationMode ?? 'date';
  const sortOrder = filters?.sortOrder ?? 'createdAt';
  const timeSpan = filters?.timeSpan ?? 'all';
  const selectedStatus = filters?.selectedStatus ?? [];
  const selectedProcessTypes = filters?.selectedProcessTypes ?? [];
  const selectedProcessStatus = filters?.selectedProcessStatus ?? [];
  const selectedOwners = filters?.selectedOwners ?? [];

  // Helper to check if a specific filter is active
  const isFilterActive = useCallback((filterKey: keyof StoredFilters): boolean => {
    if (!filters) return false;
    const value = filters[filterKey];
    if (Array.isArray(value)) return value.length > 0;
    return value !== defaultFilters[filterKey];
  }, [filters]);

  // Base styles for filter controls
  const baseFilterStyles = {
    '& .MuiInput-root': {
      color: '#e7e5e4',
      fontSize: '0.875rem', // text-sm equivalent
      '&:before': { borderColor: 'rgba(255,255,255,0.2)' },
      '&:hover:before': { borderColor: 'rgba(255,255,255,0.4)' },
      '&:after': { borderColor: '#3b82f6' },
    },
    '& .MuiSelect-icon': { color: '#a8a29e' },
  };

  // Base pill styling (always applied to prevent layout shifts)
  const basePillStyles = {
    borderRadius: '4px',
    px: 1,
    py: 0.5,
    backgroundColor: 'rgba(255, 255, 255, 0.05)', // subtle gray-900 equivalent
  };

  // Active filter highlight styles for multi-select filters
  const getFilterStyles = (filterKey: keyof StoredFilters, minWidth: number) => ({
    minWidth,
    ...baseFilterStyles,
    ...basePillStyles,
    ...(isFilterActive(filterKey) && {
      backgroundColor: 'rgba(59, 130, 246, 0.15)',
      '& .MuiInput-root:before': { borderColor: '#3b82f6' },
    }),
  });

  // Single-select filters always have a selection, so always show active styling
  const getSingleSelectStyles = (minWidth: number) => ({
    minWidth,
    ...baseFilterStyles,
    ...basePillStyles,
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    '& .MuiInput-root:before': { borderColor: '#3b82f6' },
  });

  // Menu props for dark theme dropdowns
  const darkMenuProps = {
    PaperProps: {
      sx: {
        bgcolor: '#1c1917',
        border: '1px solid rgba(255,255,255,0.1)',
        '& .MuiMenuItem-root': {
          color: '#e7e5e4',
          fontSize: '0.875rem', // text-sm equivalent
          py: 0.5,
          px: 1.5,
          minHeight: 'auto',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          '&.Mui-focusVisible': { bgcolor: 'rgba(255,255,255,0.1)' },
          '&.Mui-selected': { bgcolor: 'rgba(59, 130, 246, 0.2)' },
          '&.Mui-selected:hover': { bgcolor: 'rgba(59, 130, 246, 0.3)' },
          '&.Mui-selected.Mui-focusVisible': { bgcolor: 'rgba(59, 130, 246, 0.2)' },
        },
        '& .MuiCheckbox-root': { color: '#a8a29e' },
        '& .MuiCheckbox-root.Mui-checked': { color: '#3b82f6' },
      },
    },
  };

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  useEffect(() => {
    dispatch(setSelectedUnit(null));
  }, []);

  // Get unique values for filters
  const { statuses, processTypes, processStatuses, owners } = useMemo(() => {
    const statusSet = new Set<string>();
    const processTypeSet = new Set<string>();
    const processStatusSet = new Set<string>();
    const ownerSet = new Set<string>();

    actionItems.forEach((item) => {
      if (item.status && item.status !== 'ignored') {
        statusSet.add(item.status);
      }

      const process = processes.find(p => p.id === item.processId);
      if (process?.type) {
        processTypeSet.add(process.type);
      }
      if (process?.status) {
        processStatusSet.add(process.status);
      }

      if (item.owner?.email) {
        ownerSet.add(item.owner.email);
      }
    });

    return {
      statuses: Array.from(statusSet).sort(),
      processTypes: Array.from(processTypeSet).sort(),
      processStatuses: Array.from(processStatusSet).sort(),
      owners: Array.from(ownerSet).sort(),
    };
  }, [actionItems, processes]);

  const selectedStatusAvailable = useMemo(() => {
    const allowed = new Set(statuses);
    return selectedStatus.filter((v) => allowed.has(v));
  }, [selectedStatus, statuses]);

  const selectedProcessTypesAvailable = useMemo(() => {
    const allowed = new Set(processTypes);
    return selectedProcessTypes.filter((v) => allowed.has(v));
  }, [selectedProcessTypes, processTypes]);

  const selectedProcessStatusAvailable = useMemo(() => {
    const allowed = new Set(processStatuses);
    return selectedProcessStatus.filter((v) => allowed.has(v));
  }, [selectedProcessStatus, processStatuses]);

  const selectedOwnersAvailable = useMemo(() => {
    const allowed = new Set(owners);
    return selectedOwners.filter((v) => allowed.has(v));
  }, [selectedOwners, owners]);

  useAutoSyncMultiSelect({
    filters,
    setFilters,
    available: statuses,
    selectedKey: 'selectedStatus',
    knownKey: 'knownStatus',
  });

  useAutoSyncMultiSelect({
    filters,
    setFilters,
    available: processTypes,
    selectedKey: 'selectedProcessTypes',
    knownKey: 'knownProcessTypes',
  });

  useAutoSyncMultiSelect({
    filters,
    setFilters,
    available: processStatuses,
    selectedKey: 'selectedProcessStatus',
    knownKey: 'knownProcessStatus',
  });

  useAutoSyncMultiSelect({
    filters,
    setFilters,
    available: owners,
    selectedKey: 'selectedOwners',
    knownKey: 'knownOwners',
  });

  // Check if any filters are active
  const hasActiveFilters =
    timeSpan !== 'all' ||
    selectedStatusAvailable.length > 0 ||
    selectedProcessTypesAvailable.length > 0 ||
    selectedProcessStatusAvailable.length > 0 ||
    selectedOwnersAvailable.length > 0;

  const filteredActionItems = useMemo(() => {
    const now = new Date().getTime();
    let cutoffDate: number | null = null;

    // Calculate cutoff date based on time span
    if (timeSpan === '7days') {
      cutoffDate = now - (7 * 24 * 60 * 60 * 1000);
    } else if (timeSpan === '30days') {
      cutoffDate = now - (30 * 24 * 60 * 60 * 1000);
    } else if (timeSpan === '90days') {
      cutoffDate = now - (90 * 24 * 60 * 60 * 1000);
    }

    return actionItems.filter((action) => {
      if (action.status === 'ignored') {
        return false;
      }

      // Time span filter
      if (cutoffDate !== null) {
        const createDate = convertPseudoDateToRealDate(action.createDate);
        if (!createDate || createDate.getTime() < cutoffDate) {
          return false;
        }
      }

      // Status filter
      if (selectedStatusAvailable.length > 0 && !selectedStatusAvailable.includes(action.status || '')) {
        return false;
      }

      const process = processes.find(p => p.id === action.processId);

      // Process type filter
      if (selectedProcessTypesAvailable.length > 0) {
        if (!process || !selectedProcessTypesAvailable.includes(process.type || '')) {
          return false;
        }
      }

      // Process status filter
      if (selectedProcessStatusAvailable.length > 0) {
        if (!process || !selectedProcessStatusAvailable.includes(process.status || '')) {
          return false;
        }
      }

      // Owner filter
      if (selectedOwnersAvailable.length > 0) {
        if (!action.owner?.email || !selectedOwnersAvailable.includes(action.owner.email)) {
          return false;
        }
      }

      return true;
    });
  }, [
    actionItems,
    processes,
    timeSpan,
    selectedStatusAvailable,
    selectedProcessTypesAvailable,
    selectedProcessStatusAvailable,
    selectedOwnersAvailable,
  ]);


  // Visualization update with different modes
  useEffect(() => {
    console.log('Visualization mode changed to:', visualizationMode);
    const timeoutId = setTimeout(() => {
      if (visualizationMode === 'date') {
        // BY DATE: Color units by their earliest due date period
        const unitDateMap = new Map<string, DatePeriod>();

        filteredActionItems.forEach((actionItem) => {
          if (!actionItem.unitId) return;

          let period: DatePeriod = 'no_date';
          // TODO - this is still not good enough as it will color the all the processes for the unit in the same color.
          if (actionItem.dueDate && actionItem.status == "active") {
            // console.log('Action Item with due date for unit 9A:', actionItem);
            const dueDate = convertPseudoDateToRealDate(actionItem.dueDate);
            if (dueDate) {
              period = getDatePeriod(dueDate);
            }
          }

          // Keep the most urgent period (earlier in the period order)
          if (actionItem.status == "active" && (!unitDateMap.has(actionItem.unitId) ||
              shouldReplaceDatePeriod(unitDateMap.get(actionItem.unitId)!, period))) {
            unitDateMap.set(actionItem.unitId, period);
          }
        });

        // Create color map for date periods
        const colorMap = new Map<string, string>();
        Object.entries(DATE_PERIOD_COLORS).forEach(([period, color]) => {
          colorMap.set(period, color);
        });

        const visualizer = new GenericDistinctVisualizer(colorMap);

        const apartmentData = Array.from(unitDateMap.entries()).map(([unitId, period]) => ({
          apartmentId: unitId,
          value: period,
          label: DATE_PERIOD_CONFIGS.find(p => p.value === period)?.label || period,
        }));

        console.log('DATE MODE - Setting apartment data:', apartmentData.length, 'units');
        console.log('DATE MODE - Sample data:', apartmentData.slice(0, 3));
        dispatch(setApartmentData(apartmentData));
        dispatch(setVisualizer(visualizer));
        setTimeout(() => {
          dispatch(setHighlightedUnits(apartmentData));
        }, 100);

      } else if (visualizationMode === 'process') {
        // BY PROCESS: Color units by their active process type
        const unitProcessMap = new Map<string, string>();

        filteredActionItems.forEach((actionItem) => {
          if (!actionItem.unitId) return;

          const process = processes.find(p => p.id === actionItem.processId);
          if (process?.type) {
            if (!unitProcessMap.has(actionItem.unitId)) {
              unitProcessMap.set(actionItem.unitId, process.type);
            }
          }
        });

        const distinctTypes = new Set<string>();
        unitProcessMap.forEach((type) => distinctTypes.add(type));

        const colorMap = new Map<string, string>();
        Array.from(distinctTypes).forEach((type) => {
          const config = getProcessTypeConfig(type);
          colorMap.set(type, config.color);
        });

        const visualizer = new GenericDistinctVisualizer(colorMap);

        const apartmentData = Array.from(unitProcessMap.entries()).map(([unitId, processType]) => ({
          apartmentId: unitId,
          value: processType,
          label: getProcessTypeConfig(processType).displayName,
        }));

        console.log('PROCESS MODE - Setting apartment data:', apartmentData.length, 'units');
        dispatch(setApartmentData(apartmentData));
        dispatch(setVisualizer(visualizer));
        setTimeout(() => {
          dispatch(setHighlightedUnits(apartmentData));
        }, 100);

      } else if (visualizationMode === 'process_status') {
        // BY PROCESS STATUS: Color units by their process status
        const unitProcessStatusMap = new Map<string, string>();

        filteredActionItems.forEach((actionItem) => {
          if (!actionItem.unitId) return;

          const process = processes.find(p => p.id === actionItem.processId);
          if (process?.status) {
            // If a unit already has a status, keep the first one
            if (!unitProcessStatusMap.has(actionItem.unitId)) {
              unitProcessStatusMap.set(actionItem.unitId, process.status);
            }
          }
        });

        const distinctStatuses = new Set<string>();
        unitProcessStatusMap.forEach((status) => distinctStatuses.add(status));

        // Create color map for process statuses
        const colorMap = new Map<string, string>();
        Array.from(distinctStatuses).forEach((status) => {
          colorMap.set(status, PROCESS_STATUS_COLORS[status] || '#6B7280'); // Gray fallback
        });

        const visualizer = new GenericDistinctVisualizer(colorMap);

        const apartmentData = Array.from(unitProcessStatusMap.entries()).map(([unitId, status]) => ({
          apartmentId: unitId,
          value: status,
          label: status.charAt(0).toUpperCase() + status.slice(1), // Capitalize first letter
        }));

        console.log('PROCESS STATUS MODE - Setting apartment data:', apartmentData.length, 'units');
        dispatch(setApartmentData(apartmentData));
        dispatch(setVisualizer(visualizer));
        setTimeout(() => {
          dispatch(setHighlightedUnits(apartmentData));
        }, 100);

      } else if (visualizationMode === 'user') {
        // BY USER: Color units by task owner
        const unitOwnerMap = new Map<string, string>();

        filteredActionItems.forEach((actionItem) => {
          if (!actionItem.unitId) return;

          const ownerEmail = actionItem.owner?.email || 'unassigned';
          if (!unitOwnerMap.has(actionItem.unitId)) {
            unitOwnerMap.set(actionItem.unitId, ownerEmail);
          }
        });

        const distinctOwners = new Set<string>();
        unitOwnerMap.forEach((owner) => distinctOwners.add(owner));

        // Generate distinct colors for each owner
        const colorMap = new Map<string, string>();
        const colors = [
          '#EF4444', // Red
          '#10B981', // Green
          '#3B82F6', // Blue
          '#F59E0B', // Amber
          '#8B5CF6', // Purple
          '#EC4899', // Pink
          '#14B8A6', // Teal
          '#F97316', // Orange
          '#6366F1', // Indigo
          '#84CC16', // Lime
        ];

        Array.from(distinctOwners).forEach((owner, index) => {
          colorMap.set(owner, colors[index % colors.length]);
        });

        const visualizer = new GenericDistinctVisualizer(colorMap);

        const apartmentData = Array.from(unitOwnerMap.entries()).map(([unitId, owner]) => ({
          apartmentId: unitId,
          value: owner,
          label: owner === 'unassigned' ? 'Unassigned' : owner.split('@')[0],
        }));

        console.log('USER MODE - Setting apartment data:', apartmentData.length, 'units');
        dispatch(setApartmentData(apartmentData));
        dispatch(setVisualizer(visualizer));
        setTimeout(() => {
          dispatch(setHighlightedUnits(apartmentData));
        }, 100);
      }
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [visualizationMode, filteredActionItems, processes, dispatch, getProcessTypeConfig]);

  // Helper function to determine which date period should take priority
  function shouldReplaceDatePeriod(current: DatePeriod, newPeriod: DatePeriod): boolean {
    const priority: DatePeriod[] = ['overdue', 'this_week', 'next_week', 'week_3', 'week_4', 'week_5_plus', 'unscheduled', 'no_date'];
    return priority.indexOf(newPeriod) < priority.indexOf(current);
  }

  // Don't render until filters are loaded from localStorage
  if (!filters) {
    return null;
  }

  return (
    <div className="flex-1 overflow-auto p-[15px]">
      {/* Fixed-height filter container to prevent layout shifts */}
      <div style={{ minHeight: 100, overflow: 'hidden' }}>
        <Stack
          direction="row"
          gap={1}
          alignItems="flex-start"
          flexWrap="wrap"
          sx={{ width: 'fit-content' }}
        >
        {/* Visualization Mode Selector */}
        <FormControl size="small" variant="standard" sx={getSingleSelectStyles(160)}>
          <Select
            value={visualizationMode}
            onChange={(e) => setFilters((prev) => ({ ...prev!, visualizationMode: e.target.value as VisualizationMode }))}
            displayEmpty
            MenuProps={darkMenuProps}
          >
            <MenuItem value="date">View by Date</MenuItem>
            <MenuItem value="process">View by Process</MenuItem>
            <MenuItem value="process_status">View by Process Status</MenuItem>
            <MenuItem value="user">View by User</MenuItem>
          </Select>
        </FormControl>

        {/* Sort Dropdown */}
        <FormControl size="small" variant="standard" sx={getSingleSelectStyles(140)}>
          <Select
            value={sortOrder}
            onChange={(e) => setFilters((prev) => ({ ...prev!, sortOrder: e.target.value }))}
            displayEmpty
            MenuProps={darkMenuProps}
          >
            <MenuItem value="createdAt">Sort by Newest</MenuItem>
            <MenuItem value="dueDate">Sort by Due Date</MenuItem>
          </Select>
        </FormControl>

        {/* Time Span Filter */}
        <FormControl size="small" variant="standard" sx={getSingleSelectStyles(100)}>
          <Select
            value={timeSpan}
            onChange={(e) => setFilters((prev) => ({ ...prev!, timeSpan: e.target.value }))}
            displayEmpty
            MenuProps={darkMenuProps}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="7days">Last 7 Days</MenuItem>
            <MenuItem value="30days">Last 30 Days</MenuItem>
            <MenuItem value="90days">Last 90 Days</MenuItem>
          </Select>
        </FormControl>

        {/* Status Filter */}
        {statuses.length > 0 && (
          <FormControl size="small" variant="standard" sx={getFilterStyles('selectedStatus', 100)}>
            <Select
              multiple
              value={selectedStatusAvailable}
              onChange={(e) => setFilters((prev) => ({ ...prev!, selectedStatus: e.target.value as string[] }))}
              displayEmpty
              MenuProps={darkMenuProps}
              renderValue={(selected) =>
                selected.length === 0 ? 'Status' :
                selected.length === 1 ? selected[0] :
                `Status (${selected.length})`
              }
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={selectedStatusAvailable.includes(status)} size="small" sx={{ p: 0.5, mr: 0.5 }} />
                  <Typography sx={{ fontSize: '0.875rem' }}>{status}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Process Type Filter */}
        {processTypes.length > 0 && (
          <FormControl size="small" variant="standard" sx={getFilterStyles('selectedProcessTypes', 120)}>
            <Select
              multiple
              value={selectedProcessTypesAvailable}
              onChange={(e) => setFilters((prev) => ({ ...prev!, selectedProcessTypes: e.target.value as string[] }))}
              displayEmpty
              MenuProps={darkMenuProps}
              renderValue={(selected) => {
                if (selected.length === 0) return 'Process Type';
                if (selected.length === 1) {
                  const config = getProcessTypeConfig(selected[0]);
                  return (
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '2px',
                          backgroundColor: config.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: '0.875rem' }}>{config.displayName}</Typography>
                    </Stack>
                  );
                }
                return `Process Type (${selected.length})`;
              }}
            >
              {processTypes.map((type) => {
                const config = getProcessTypeConfig(type);
                return (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={selectedProcessTypesAvailable.includes(type)} size="small" sx={{ p: 0.5, mr: 0.5 }} />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '2px',
                        backgroundColor: config.color,
                        flexShrink: 0,
                        mr: 0.5,
                      }}
                    />
                    <Typography sx={{ fontSize: '0.875rem' }}>{config.displayName}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}

        {/* Process Status Filter */}
        {processStatuses.length > 0 && (
          <FormControl size="small" variant="standard" sx={getFilterStyles('selectedProcessStatus', 130)}>
            <Select
              multiple
              value={selectedProcessStatusAvailable}
              onChange={(e) => setFilters((prev) => ({ ...prev!, selectedProcessStatus: e.target.value as string[] }))}
              displayEmpty
              MenuProps={darkMenuProps}
              renderValue={(selected) =>
                selected.length === 0 ? 'Process Status' :
                selected.length === 1 ? selected[0] :
                `Process Status (${selected.length})`
              }
            >
              {processStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={selectedProcessStatusAvailable.includes(status)} size="small" sx={{ p: 0.5, mr: 0.5 }} />
                  <Typography sx={{ fontSize: '0.875rem' }}>{status}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Owner Filter */}
        {owners.length > 0 && (
          <FormControl size="small" variant="standard" sx={getFilterStyles('selectedOwners', 120)}>
            <Select
              multiple
              value={selectedOwnersAvailable}
              onChange={(e) => setFilters((prev) => ({ ...prev!, selectedOwners: e.target.value as string[] }))}
              displayEmpty
              MenuProps={darkMenuProps}
              renderValue={(selected) =>
                selected.length === 0 ? 'Owner' :
                selected.length === 1 ? selected[0].split('@')[0] :
                `Owner (${selected.length})`
              }
            >
              {owners.map((owner) => (
                <MenuItem key={owner} value={owner}>
                  <Checkbox checked={selectedOwnersAvailable.includes(owner)} size="small" sx={{ p: 0.5, mr: 0.5 }} />
                  <Typography sx={{ fontSize: '0.875rem' }}>{owner}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <IconButton
            size="small"
            onClick={handleClearFilters}
            sx={{
              color: '#a8a29e',
              '&:hover': { color: '#e7e5e4', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
            title="Clear all filters"
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
        </Stack>
      </div>

      <ActionGroupList
        actionItems={filteredActionItems}
        onClickActionItem={onClickActionItem}
        onBuildingDataChange={onBuildingDataChange}
        sortOrder={sortOrder}
        isLoading={isLoading}
        onHoverItem={(item) => {
          // console.log('tracker tab content Hovered', item);
          dispatch(setHoveredActionItem(item));
        }}
      />
    </div>
  );
}
